/**
 * The public demo's one door (exception M1; docs/PLAN-public-demo-20261030.md) — the only place outside src/demo/ that
 * the demo's runtime is reached from (src/index.tsx renders `<DemoGate>` on a demo page, nothing else does), and where
 * App.tsx takes the demo's notice from (`<DemoBandSource>`, below), which draws and loads nothing off a demo page.
 *
 * The runtime (src/demo/runtime/) loads as its own `demo-runtime` chunk, and only on a demo page: its engine, its
 * scripted server and the industries' content never ride the first download. The app draws once the runtime has its
 * server in place, so the app's first effects already find every answer they ask for. If the runtime cannot load or
 * start, the page says so with the boot's own failure line (src/demo/boot/isolate.ts) — it never falls back to the real
 * app.
 */
import React, { useEffect, useState } from 'react';
import { useDeferredModule } from '../utils/useDeferredModule';
import { isDemoPage } from './demoAddress';
import { DEMO_LOAD_FAILED_LINE } from './boot/isolate';
import type * as RuntimeNamespace from './runtime/runtime';
import type * as DemoBandNamespace from './band/DemoBand';

export { isDemoPage };

type RuntimeModule = typeof RuntimeNamespace;

const loadRuntime = (): Promise<RuntimeModule> => import(/* webpackChunkName: "demo-runtime" */ './runtime/runtime');

const loadBand = (): Promise<typeof DemoBandNamespace> => import(/* webpackChunkName: "demo-band" */ './band/DemoBand');

const DemoBandLoader: React.FC = () => {
  const { module } = useDeferredModule(loadBand, 'demo-band', { start: 'mount' });
  const Band = module?.DemoBand;
  return Band ? <Band /> : null;
};

/**
 * The demo's notice in the app's own band (docs/PLAN-public-demo-20261030.md §9): one of the band's sources, which
 * App.tsx lists inside GlobalBannerCenter like every other notice (a source registers with the band from inside it).
 * Off a demo page it draws nothing and loads nothing; on one it loads the band's chunk (./band/DemoBand.tsx), which
 * registers the notice.
 */
export const DemoBandSource: React.FC = () => (isDemoPage() ? <DemoBandLoader /> : null);

const failedStyle: React.CSSProperties = {
  maxWidth: 420,
  margin: '20vh auto 0',
  padding: '0 16px',
  font: '500 16px/1.5 system-ui, sans-serif',
  textAlign: 'center',
};

export const DemoGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { module, error } = useDeferredModule(loadRuntime, 'demo-runtime', { start: 'mount' });
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!module) return undefined;
    let alive = true;
    module.startDemo().then(
      () => alive && setReady(true),
      (e: unknown) => {
        // eslint-disable-next-line no-console
        console.error('[demo] the runtime did not start', e);
        if (alive) setFailed(true);
      },
    );
    return () => {
      alive = false;
    };
  }, [module]);

  if (error || failed) {
    return (
      <div role="alert" style={failedStyle}>
        {DEMO_LOAD_FAILED_LINE}
      </div>
    );
  }
  if (!ready) return null;
  return <>{children}</>;
};
